import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "@/i18n/I18nProvider";

const STORAGE_KEY = "elq_consent_v2";

function applyConsent(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {}
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      ad_storage: state.ad_storage,
      ad_user_data: state.ad_user_data,
      ad_personalization: state.ad_personalization,
      analytics_storage: state.analytics_storage,
    });
  }
}

export function CookieBanner({ openSignal }) {
  const { t, lang } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [prefs, setPrefs] = useState({
    analytics: false,
    marketing: false,
    preferences: true,
  });

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) setVisible(true);
    } catch (e) {
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    if (openSignal > 0) {
      setVisible(true);
      setCustomize(true);
    }
  }, [openSignal]);

  if (!visible) return null;

  const acceptAll = () => {
    applyConsent({
      ad_storage: "granted",
      ad_user_data: "granted",
      ad_personalization: "granted",
      analytics_storage: "granted",
    });
    setVisible(false);
  };

  const rejectAll = () => {
    applyConsent({
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "denied",
    });
    setVisible(false);
  };

  const save = () => {
    applyConsent({
      ad_storage: prefs.marketing ? "granted" : "denied",
      ad_user_data: prefs.marketing ? "granted" : "denied",
      ad_personalization: prefs.marketing ? "granted" : "denied",
      analytics_storage: prefs.analytics ? "granted" : "denied",
    });
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      className="fixed inset-x-4 bottom-4 md:left-6 md:right-auto md:bottom-6 md:max-w-lg z-50 bg-slate-900 text-white p-6 rounded-xl shadow-2xl border border-slate-700"
      data-testid="cookie-banner-container"
    >
      <h3 className="text-base font-semibold mb-2">{t("cookie_banner.title")}</h3>
      <p className="text-sm text-slate-300 leading-relaxed mb-4">{t("cookie_banner.body")}</p>

      {customize && (
        <div className="space-y-3 mb-4 border-t border-slate-700 pt-4">
          <PrefRow
            title={t("cookie_banner.cat_necessary_title")}
            desc={t("cookie_banner.cat_necessary_desc")}
            checked
            disabled
          />
          <PrefRow
            title={t("cookie_banner.cat_analytics_title")}
            desc={t("cookie_banner.cat_analytics_desc")}
            checked={prefs.analytics}
            onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
            testId="cookie-pref-analytics"
          />
          <PrefRow
            title={t("cookie_banner.cat_marketing_title")}
            desc={t("cookie_banner.cat_marketing_desc")}
            checked={prefs.marketing}
            onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
            testId="cookie-pref-marketing"
          />
          <PrefRow
            title={t("cookie_banner.cat_preferences_title")}
            desc={t("cookie_banner.cat_preferences_desc")}
            checked={prefs.preferences}
            onChange={(v) => setPrefs((p) => ({ ...p, preferences: v }))}
            testId="cookie-pref-preferences"
          />
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {!customize ? (
          <>
            <button
              type="button"
              onClick={acceptAll}
              className="flex-1 min-w-[140px] bg-amber-500 hover:bg-amber-400 text-slate-950 text-sm font-semibold px-4 py-2 rounded-md"
              data-testid="cookie-accept-all-button"
            >
              {t("cookie_banner.accept_all")}
            </button>
            <button
              type="button"
              onClick={rejectAll}
              className="flex-1 min-w-[140px] bg-slate-800 hover:bg-slate-700 text-white text-sm font-semibold px-4 py-2 rounded-md border border-slate-600"
              data-testid="cookie-reject-all-button"
            >
              {t("cookie_banner.reject_all")}
            </button>
            <button
              type="button"
              onClick={() => setCustomize(true)}
              className="text-sm text-slate-300 hover:text-white underline underline-offset-2 px-2 py-2"
              data-testid="cookie-customize-button"
            >
              {t("cookie_banner.customize")}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={save}
              className="flex-1 min-w-[140px] bg-amber-500 hover:bg-amber-400 text-slate-950 text-sm font-semibold px-4 py-2 rounded-md"
              data-testid="cookie-save-preferences-button"
            >
              {t("cookie_banner.save")}
            </button>
            <button
              type="button"
              onClick={rejectAll}
              className="flex-1 min-w-[140px] bg-slate-800 hover:bg-slate-700 text-white text-sm font-semibold px-4 py-2 rounded-md border border-slate-600"
              data-testid="cookie-reject-all-button"
            >
              {t("cookie_banner.reject_all")}
            </button>
            <button
              type="button"
              onClick={acceptAll}
              className="text-sm text-slate-300 hover:text-white underline underline-offset-2 px-2 py-2"
              data-testid="cookie-accept-all-button"
            >
              {t("cookie_banner.accept_all")}
            </button>
          </>
        )}
      </div>

      <div className="text-xs text-slate-400 mt-3">
        <Link
          to={`/${lang}/cookie-policy`}
          className="hover:text-white underline"
          data-testid="cookie-learn-more"
        >
          {t("cookie_banner.learn_more")}
        </Link>
      </div>
    </div>
  );
}

function PrefRow({ title, desc, checked, onChange, disabled, testId }) {
  return (
    <label className="flex items-start gap-3 text-sm">
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 accent-amber-500"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.checked)}
        data-testid={testId}
      />
      <span>
        <span className="block text-white font-medium">{title}</span>
        <span className="block text-slate-400 text-xs">{desc}</span>
      </span>
    </label>
  );
}

export default CookieBanner;
